import type { TenantClosure } from "@/lib/types.js";

/**
 * Format a Date as YYYY-MM-DD in the tenant timezone. We use the `en-CA`
 * locale because it natively renders ISO-style dates ("2026-12-25"), so
 * lexicographic comparison ordering matches calendar order. Mirrors the
 * helper used in the original calendar.tsx (PRD closures-and-holidays
 * REQ-CL-009 OQ-CL-3 — closures are tenant-local).
 */
export function tenantDayLabel(d: Date | string, tenantTz: string): string {
  const date = d instanceof Date ? d : new Date(d);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tenantTz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(date);
}

/**
 * Given an array of closures and a target tenant-local day label, return the
 * first matching closure's id+reason (or null if the day is open).
 *
 * The exclusive-end `to` (e.g. local midnight) belongs to the NEXT day; we
 * subtract 1 ms before formatting so a 24h closure spans exactly one day-
 * label. Returns the FIRST match — `expandClosures` server-side is sorted by
 * `from` so this is alphabetically stable.
 */
export function pickDayClosure(
  closures: TenantClosure[],
  dayLabel: string,
  tenantTz: string
): { id: string; reason: string } | null {
  for (const closure of closures) {
    if (closure.deleted_at) continue;
    for (const occ of closure.occurrences_in_window) {
      const fromLabel = tenantDayLabel(occ.from, tenantTz);
      const toLabel = tenantDayLabel(new Date(Date.parse(occ.to) - 1), tenantTz);
      if (dayLabel >= fromLabel && dayLabel <= toLabel) {
        return { id: closure.id, reason: closure.reason };
      }
    }
  }
  return null;
}
